import React from 'react'
import styles from '../styles/about.module.css'
import { PieChart, Pie, Cell, Legend, Tooltip } from 'recharts';
import about2 from '../images/about2.jpg'
import chapter from '../images/chapter.jpg'
import { GrGroup } from "react-icons/gr"
import { LuGraduationCap } from "react-icons/lu"
import { RiServiceLine } from "react-icons/ri"


export const yearData = [
  { name: '1st Year', value: 4 },
  { name: '2nd Year', value: 6 },
  { name: '3rd Year', value: 23 },
  { name: '4th+ Year', value: 18 },
];

export const majorData = [
  { name: 'MSE', value: 1 },
  { name: 'CHEME', value: 2 },
  { name: 'EE', value: 3 },
  { name: 'ENVE', value: 6 },
  { name: 'BIEN', value: 9 },
  { name: 'ME', value: 11 },
  { name: 'CS/CSBA/CE', value: 18 },
];

const redColors = ['#E4C2BB', '#D49C9C', '#C87B7B', '#A84A4A', '#822525', '#6E1E1E', '#4A1212'];

const renderLegend = (props) => {
	const { payload } = props;

	return (
		<ul className={styles.legend}>
			{payload.map((entry, index) => (
				<li className={styles.legendItem} key={`item-${index}`}>
					<svg className={styles.dot}>
						<circle cx="5" cy="5" r="5" fill={entry.color} />
					</svg>
					<span className={styles.legendText}>{entry.value}</span>
				</li>
			))}
		</ul>
	);
};

const CustomTooltip = ({ active, payload }) => {
	if (active && payload && payload.length) {
		const total = payload[0].payload.total;
		const percentage = Math.round((payload[0].value / total) * 100);

		return (
			<div className={styles.tooltip}>
				<p>{`${payload[0].name}: ${payload[0].value} (${percentage}%)`}</p>
			</div>
		)
	}
	return null
}

const About = () => {
	const yearTotal = yearData.reduce((sum, item) => sum + item.value, 0);
	const majorTotal = majorData.reduce((sum, item) => sum + item.value, 0);

	const yearDataWithTotal = yearData.map(item => ({...item, total: yearTotal}));
	const majorDataWithTotal = majorData.map(item => ({...item, total: majorTotal}));

	// Add this function to handle responsive sizing
	const getChartDimensions = () => {
		return window.innerWidth <= 1150 ? {
			width: 300,
			height: 200,
			outerRadius: 70,
			innerRadius: 40
		} : {
			width: 400,
			height: 300,
			outerRadius: 110,
			innerRadius: 60
		}
	}

	const [dimensions, setDimensions] = React.useState(getChartDimensions())

	React.useEffect(() => {
		const handleResize = () => {
			setDimensions(getChartDimensions())
		}

		window.addEventListener('resize', handleResize)
		return () => window.removeEventListener('resize', handleResize)
	}, [])

	const activeMembers = yearTotal;
	const disciplines = majorData.length;

	return (
		<div className={styles.about}>
			{/* Hero Section */}
			<section className={styles.hero}>
				<img src={about2} alt="Theta Tau members" className={styles.heroImage} />
				<div className={styles.heroOverlay}>
					<div className="hero-fade">
						<blockquote className={styles.heroQuote}>
							"The purpose of Theta Tau is to develop and maintain a high standard of professional interest among its members and to unite them in a strong bond of fraternal fellowship."
						</blockquote>
						<div className={styles.heroAttrib}>Sigma Delta Chapter · UC Riverside</div>
					</div>
				</div>
			</section>

			{/* History Section */}
			<section className={styles.historySection}>
				<div className={`${styles.historyContent} reveal`}>
					<div className={styles.historyText}>
						<span className={styles.eyebrow}>Since 1904</span>
						<h1 className={styles.heading}>Our History</h1>
						<div className={styles.rule}></div>
						<p className={styles.text}>
							Theta Tau was originally founded as the "Society of Hammer and Tongs" on October 15, 1904 at the University of Minnesota by four engineers: Erich J. Schrader, Elwin L. Vinal, William M. Lewis, and Isaac B. Hanks. At the fraternity's first national convention in 1911, the name was changed to Theta Tau, and the fraternity opened up to include all departments of engineering. Since then, approximately 30,000 engineering students have been initiated.
						</p>
					</div>
					<div className={styles.historyImageWrapper}>
						<span className={styles.estAccent}>Fall 2025</span>
						<img src={chapter} alt="Theta Tau chapter" className={styles.historyImage} />
					</div>
				</div>
			</section>

			{/* Three Pillars Section */}
			<section className={styles.pillarsSection}>
				<div className={`${styles.pillarsHead} reveal`}>
					<h1 className={styles.heading}>Our Three Pillars</h1>
					<div className={`${styles.rule} ${styles.center}`}></div>
				</div>
				<div className={styles.pillarsContainer}>
					<div className={`${styles.pillarCard} reveal d1`}>
						<div className={styles.pillarIcon}><GrGroup /></div>
						<h3 className={styles.pillarTitle}>Brotherhood</h3>
						<div className={styles.pillarUnderline}></div>
						<p className={styles.pillarDescription}>
							We cultivate lifelong bonds between members through shared experiences, mutual support, and a commitment to each other's growth. Our brotherhood extends beyond college — it is a network of engineers who uplift one another throughout their careers and lives.
						</p>
					</div>
					<div className={`${styles.pillarCard} reveal d2`}>
						<div className={styles.pillarIcon}><LuGraduationCap /></div>
						<h3 className={styles.pillarTitle}>Profession</h3>
						<div className={styles.pillarUnderline}></div>
						<p className={styles.pillarDescription}>
							We prepare our members for the engineering profession through workshops, industry connections, and mentorship. From resume reviews to company tours, we bridge the gap between academic knowledge and real-world professional excellence.
						</p>
					</div>
					<div className={`${styles.pillarCard} reveal d3`}>
						<div className={styles.pillarIcon}><RiServiceLine /></div>
						<h3 className={styles.pillarTitle}>Service</h3>
						<div className={styles.pillarUnderline}></div>
						<p className={styles.pillarDescription}>
							We give back to our community through volunteer work, philanthropy events, and outreach programs. Our members dedicate their skills and time to making a positive impact on campus and in the broader Riverside community.
						</p>
					</div>
				</div>
			</section>

			{/* Stats + Charts Section */}
			<section className={styles.statsSection}>
				<div className={`${styles.statsHead} reveal`}>
					<h1 className={styles.heading}>Chapter Breakdown</h1>
					<div className={`${styles.rule} ${styles.center}`}></div>
				</div>
				<div className={`${styles.statsHighlights} reveal d1`}>
					<div className={styles.statItem}>
						<span className={styles.statNumber}>{activeMembers}</span>
						<span className={styles.statLabel}>Active Members</span>
					</div>
					<div className={styles.statItem}>
						<span className={styles.statNumber}>{disciplines}</span>
						<span className={styles.statLabel}>Engineering Disciplines</span>
					</div>
					<div className={styles.statItem}>
						<span className={styles.statNumber}>1904</span>
						<span className={styles.statLabel}>Founded</span>
					</div>
				</div>
				<div className={`${styles.breakdownContainer} reveal d2`}>
					<div className={styles.chartContainer}>
						<h3 className={styles.chartTitle}>Year Distribution</h3>
						<PieChart className={styles.chart} width={dimensions.width} height={dimensions.height}>
							<Pie data={yearDataWithTotal} dataKey="value" outerRadius={dimensions.outerRadius} innerRadius={dimensions.innerRadius} strokeWidth={2}>
								{yearDataWithTotal.map((entry, i) => (
									<Cell key={`cell-${i}`} fill={redColors[i % redColors.length]} />
								))}
							</Pie>
							<Tooltip content={<CustomTooltip />} />
							<Legend content={renderLegend} layout="vertical" align="right" verticalAlign="middle" wrapperStyle={{ fontSize: "14px", lineHeight: "1.5" }}/>
						</PieChart>
					</div>
					<div className={styles.chartContainer}>
						<h3 className={styles.chartTitle}>Major Distribution</h3>
						<PieChart className={styles.chart} width={dimensions.width} height={dimensions.height}>
							<Pie data={majorDataWithTotal} dataKey="value" outerRadius={dimensions.outerRadius} innerRadius={dimensions.innerRadius} strokeWidth={2}>
								{majorDataWithTotal.map((entry, i) => (
									<Cell key={`cell-${i}`} fill={redColors[i % redColors.length]} />
								))}
							</Pie>
							<Tooltip content={<CustomTooltip />} />
							<Legend content={renderLegend} layout="vertical" align="right" verticalAlign="middle" wrapperStyle={{ fontSize: "14px", lineHeight: "1.5" }}/>
						</PieChart>
					</div>
				</div>
			</section>
		</div>
	)
}

export default About
